// store/Spreadsheet/cellChangesSlice.ts
//TODO: current separator is /// think of something better
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Column, Row } from '../../types/Spreadsheet';
import { RootState } from '@store/store';

export type CellStatusType = 'none' | 'unsaved' | 'saved' | 'error';


export interface CellStatus {
  status: CellStatusType;
  originalValue: any;
  pendingValue?: any;
}

type CellChangesState = Record<string, CellStatus>;

const initialState: CellChangesState = {};

function makeCellKey( rowId: string, colId: string ) {
  return `${rowId}///${colId}`;
}

function clearAll( state: CellChangesState ) {
  Object.keys( state ).forEach(( key ) => {
    delete state[key];
  });
}

// ---------------------------------------------------------------------------
// Value equality (TV1-1690 revert-to-original fix).
//
// Cells carry primitives, arrays (multi-select, multi-string, multi-number),
// and structured objects (image URLs, multi-number-unit). A naive `===` check
// would treat ['a','b'] === ['a','b'] (different references) as different and
// mark the cell dirty forever. We need structural equality.
//
// We also need to treat null / undefined / '' as the same empty value: an
// empty cell flows through code paths as any of these three, and a no-op
// edit between them must NOT mark the cell dirty.
// ---------------------------------------------------------------------------

const normalizeForEquality = ( v: any ): any =>
  ( v === undefined || v === null || v === '' ) ? null : v;

const isEqualValue = ( a: any, b: any ): boolean => {
  if ( a === b ) return true;
  if ( a == null || b == null ) return a === b;
  if ( typeof a !== typeof b ) return false;
  if ( typeof a !== 'object' ) return a === b;
  if ( Array.isArray( a )) {
    if ( !Array.isArray( b ) || a.length !== b.length ) return false;
    for ( let i = 0; i < a.length; i++ ) {
      if ( !isEqualValue( a[i], b[i])) return false;
    }
    return true;
  }
  if ( Array.isArray( b )) return false;
  const ak = Object.keys( a );
  const bk = Object.keys( b );
  if ( ak.length !== bk.length ) return false;
  for ( const k of ak ) {
    if ( !isEqualValue( a[k], b[k])) return false;
  }
  return true;
};

// Compare two cell values treating empty-equivalents (null/undefined/'') as
// the same value. Exported for tests; the slice uses it internally only.
export const isSameCellValue = ( a: any, b: any ): boolean =>
  isEqualValue( normalizeForEquality( a ), normalizeForEquality( b ));

export const cellChangesSlice = createSlice({
  name: 'cellChanges',
  initialState,
  reducers: {
    loadInitialData( state, action: PayloadAction<Row[]> ) {
      clearAll( state );
    },
    cellValueChanged( state, action: PayloadAction<{ rowId: string; columnId: string; oldValue: any; newValue: any; }> ) {
      const { rowId, columnId, oldValue, newValue } = action.payload;
      const cellKey = makeCellKey( rowId, columnId );
      const existingEntry = state[cellKey];

      // The ORIGINAL value (pre-session) is the value captured the first time
      // this cell was edited. On subsequent edits we must compare against
      // THAT, not against the per-action `oldValue` (which is the value the
      // user is editing FROM, i.e. the prior pending value). This is what
      // makes X -> Y -> X round-trip to clean.
      const original = existingEntry ? existingEntry.originalValue : oldValue;

      if ( isSameCellValue( newValue, original )) {
        // Reverted to original — drop the entry entirely. Absent keys read
        // as status 'none' via selectCellStatus, so consumers see a clean
        // cell and the dirty count returns to its pre-edit level.
        if ( existingEntry ) delete state[cellKey];
        return;
      }

      state[cellKey] = {
        status: 'unsaved',
        originalValue: original,
        pendingValue: newValue
      };
    },
    persistSuccess( state, action: PayloadAction<{ rows: Row[] }> ) {
      const updatedRows = action.payload.rows;
      const rowsById = Object.fromEntries( updatedRows.map(( r ) => [ r.id, r ]));
      for ( const [ cellKey, cellStatus ] of Object.entries( state )) {
        if ( cellStatus.status === 'unsaved' ) {
          const [ rowId, colId ] = cellKey.split( '///' );
          const row = rowsById[rowId];
          if ( !row ) continue;
          
          const newServerValue = row.values[colId];
          if ( cellStatus.pendingValue === newServerValue ) {
            cellStatus.status = 'saved';
            cellStatus.originalValue = newServerValue;
          } else {
            cellStatus.status = 'error';
            cellStatus.originalValue = newServerValue;
          }
          delete cellStatus.pendingValue;
        }
      }
    },
    persistFail( state ) {
      for ( const cellKey of Object.keys( state )) {
        const cell = state[cellKey];
        if ( cell.status === 'unsaved' ) {
          cell.status = 'error';
        }
      }
    },
    reloadFromServer( state, action: PayloadAction<{ rows: Row[] }> ) {
      clearAll( state );
    },
    rowAdded( state, action: PayloadAction<{ row: Row; columns: Column[]; }> ) {
      const { row, columns } = action.payload;
      columns.forEach(( col ) => {
        const cellKey = makeCellKey( row.id, col.id );

        const insertedValue = row.values[col.id];
        state[cellKey] = { status: 'unsaved', originalValue: undefined, pendingValue: insertedValue };
      });
    },
    bulkCellValueChanged: ( state, action: PayloadAction< Array<{ rowId: string; columnId: string; oldValue: any; newValue: any; }> > ) => {
      // Per-entry: same logic as cellValueChanged. The pre-fix version
      // collapsed every entry into status 'none' on equality, leaving stale
      // 'none' records around; we now drop them so the dirty count and any
      // selectors that key off entry presence (status badges, save buttons)
      // stay accurate after a paste that no-ops some cells.
      action.payload.forEach(({ rowId, columnId, oldValue, newValue }) => {
        const cellKey = makeCellKey( rowId, columnId );
        const existingEntry = state[cellKey];
        const original = existingEntry ? existingEntry.originalValue : oldValue;

        if ( isSameCellValue( newValue, original )) {
          if ( existingEntry ) delete state[cellKey];
          return;
        }

        state[cellKey] = {
          status: 'unsaved',
          originalValue: original,
          pendingValue: newValue
        };
      });
    },
    clearSaved( state ) {
      for ( const cellKey of Object.keys( state )) {
        const cell = state[cellKey];
        if ( cell.status === 'saved' ) {
          delete state[cellKey];
        }
      }
    }
  }
});

export const {
  loadInitialData,
  cellValueChanged,
  persistSuccess,
  persistFail,
  reloadFromServer,
  rowAdded,
  bulkCellValueChanged,
  clearSaved
} = cellChangesSlice.actions;

export default cellChangesSlice.reducer;
export const selectCellStatus = ( rowId: string, colId: string ) => ( state: RootState ) => {
  const cellKey = `${rowId}///${colId}`;
  const presentCellChanges = state.spreadsheet.cellChanges?.present ?? {};
  return presentCellChanges[cellKey]?.status || 'none';
};

// Whole cell-changes map (present slice of redux-undo). Used by DataRow to do
// per-cell status lookups in a single subscription, replacing the per-cell
// `selectCellStatus( rowId, colId )` subscriptions that previously ran inside
// Cell.tsx (TV1-1690 / B-5c).
//
// NOTE: keys here use the `///` separator owned by this slice, NOT `getCellKey`
// from `utils/grid.ts`. That mismatch is a pre-existing concern tracked in
// docs/prs/TV1-1690/concerns.md (selectRowStatus separator mismatch). Do NOT
// migrate the encoding here — that's a separate ticket. DataRow encodes lookup
// keys the same way (`${rowId}///${columnId}`) to read this map.
const EMPTY_CELL_CHANGES: CellChangesState = {};
export const selectCellChangesMap = ( state: RootState ): CellChangesState =>
  state.spreadsheet.cellChanges?.present ?? EMPTY_CELL_CHANGES;

// Same separator as makeCellKey() inside this slice — DataRow uses this helper
// to compute the lookup key for `selectCellChangesMap`.
export const cellChangesKey = ( rowId: string, colId: string ): string => `${rowId}///${colId}`;